// Pacesetter web terminal ("Pacesetter") — /pacesetter. Standalone top-level
// page (not nested under a layout's <Outlet>) — mirrors Reception.tsx/
// Config.tsx: fetch the user itself, render its own Header.
//
// xterm.js talks to the terminal bridge over a binary WebSocket (both
// directions); a JSON *text* frame carries resize control. The WebSocket
// handshake (GET /api/pacesetter/ws) is the authoritative server-side gate
// (feature flag -> 404, permission -> 403, bad Origin -> 403, session caps ->
// 429) — `canPacesetter` below is a client-side UX guard only (hide the nav
// + this page for a role/feature combo that would fail the handshake
// anyway), never the real security boundary.
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button } from '@tremor/react';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';
import Header from '../components/Header';
import AccessDenied from '../components/AccessDenied';
import { currentUser } from '../api/auth';
import { getPacesetterTheme, pacesetterImageUrl, pacesetterWsUrl, VT220_KEYS } from '../api/pacesetter';
import { themeToXterm } from '../lib/pacesetterTheme';
import { canPacesetter } from '../lib/roles';
import { t } from '../lib/i18n';
import type { PacesetterTheme, User } from '../api/types';

type ConnStatus = 'connecting' | 'connected' | 'closed';

/**
 * Maps a physical keydown's `event.key` to its VT220_KEYS entry, if the shim
 * owns that key — the thin key-map shim the design spec calls for (rewrite
 * ONLY the offending keys, not a speculative full keymap). F1-F4 are vt220's
 * PF1-PF4: there's no separate PF key on a modern keyboard, and sending
 * ESC O P/Q/R/S for F1-F4 is the standard vt100/220 terminal-emulator
 * convention (VT220_KEYS.PF1 etc. already hold those exact bytes). Returns
 * undefined for keys the shim doesn't own, so the caller falls through to
 * xterm's default handling. Exported (pure, no DOM) so Pacesetter.test.tsx
 * can assert the mapping directly without simulating a real keydown.
 */
const KEYDOWN_MAP: Record<string, keyof typeof VT220_KEYS> = {
  F1: 'PF1', F2: 'PF2', F3: 'PF3', F4: 'PF4',
  // F5 -> ESC O T (Pacesetter's vt220v7kf logical F5). We take it over so the
  // preventDefault below suppresses the browser's page-refresh; the on-screen
  // F5 button is the guaranteed path if a browser refuses to yield the key.
  F5: 'F5',
  F6: 'F6', F7: 'F7', F8: 'F8', F9: 'F9', F10: 'F10', F11: 'F11', F12: 'F12',
  ArrowUp: 'Up', ArrowDown: 'Down', ArrowLeft: 'Left', ArrowRight: 'Right',
  Enter: 'Enter', Escape: 'Esc',
};
export function vt220KeyForEvent(key: string): string | undefined {
  const logical = KEYDOWN_MAP[key];
  return logical ? VT220_KEYS[logical] : undefined;
}

function toBytes(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

export default function Pacesetter() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    // Full-screen terminal app: exactly the viewport height, no page
    // scrollbar. The terminal area below is flex-1 and absorbs the height;
    // nothing on this page should ever need the page itself to scroll.
    <div className="h-screen overflow-hidden flex flex-col">
      <Header user={user} />
      {canPacesetter(user) ? <PacesetterTabs /> : (
        <main className="flex-1 px-6 py-6 max-w-screen-2xl mx-auto w-full">
          <AccessDenied user={user} />
        </main>
      )}
    </div>
  );
}

// Soft cap on simultaneous tabs — each one is its own WebSocket + backend
// PTY, so this is a courtesy limit (not a hard resource constraint), just
// enough to stop a user from accidentally spawning dozens of sessions.
const MAX_TABS = 8;

/** Tab-strip dot color for a tab's connection status; undefined (not yet
 *  reported by a freshly-mounted terminal) reads the same as "connecting". */
function dotColor(status: ConnStatus | undefined): string {
  if (status === 'connected') return 'bg-emerald-500';
  if (status === 'closed') return 'bg-red-500';
  return 'bg-amber-400';
}

function dotTitle(status: ConnStatus | undefined): string {
  if (status === 'connected') return t.pacesetter.status.connected;
  if (status === 'closed') return t.pacesetter.status.closed;
  return t.pacesetter.status.connecting;
}

/**
 * Tab manager for the Pacesetter terminal — lets an operator run several
 * independent sessions side by side (e.g. one per store/task) without
 * losing any of them when switching. Each tab is its own WebSocket -> its
 * own backend PTY (see PacesetterTerminal), so "tabs" here just means
 * mounting several keep-alive PacesetterTerminal instances and toggling
 * which one is visible.
 *
 * Every tab's terminal stays MOUNTED at all times (`hidden` via display:none
 * on the inactive ones, never unmounted) — unmounting would tear down that
 * tab's WebSocket and kill its PTY. This is only safe because each terminal
 * is a fixed 80x24 grid with no FitAddon-to-container size dependency, so an
 * invisible (zero-rendered-space) terminal doesn't need to resize when it
 * becomes visible again.
 *
 * Each tab's connection status is tracked here (statuses, keyed by tab id)
 * and shown as a small colored dot in the tab strip — this replaces the old
 * per-terminal status-bar row (removed from PacesetterTerminal to reclaim
 * that vertical space).
 */
function PacesetterTabs() {
  const [tabs, setTabs] = useState<number[]>([1]);
  const [active, setActive] = useState(1);
  // Monotonic id counter — never reused, even after a tab closes, so a
  // stale ref/effect from a just-closed tab can't collide with a new one.
  const nextIdRef = useRef(2);
  const [statuses, setStatuses] = useState<Record<number, ConnStatus>>({});

  function addTab() {
    if (tabs.length >= MAX_TABS) return;
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    setTabs([...tabs, id]);
    setActive(id);
  }

  function closeTab(id: number) {
    const idx = tabs.indexOf(id);
    const nextTabs = tabs.filter((tabId) => tabId !== id);
    if (nextTabs.length === 0) return; // never allow zero tabs (UI also hides × when tabs.length<=1)
    if (active === id) {
      // Fall back to a neighbor — the previous index, or the new first tab
      // if we just closed index 0.
      setActive(nextTabs[Math.max(0, idx - 1)]);
    }
    setTabs(nextTabs);
    setStatuses((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-4 py-1.5 flex items-center gap-1 flex-wrap border-b border-tremor-border">
        {tabs.map((id) => (
          <div key={id} className="flex items-center">
            <span
              className={'inline-block w-2 h-2 rounded-full mr-1.5 ' + dotColor(statuses[id])}
              title={dotTitle(statuses[id])}
            />
            <button
              type="button"
              onClick={() => setActive(id)}
              className={
                'text-sm px-2 py-1 rounded ' +
                (id === active
                  ? 'bg-tremor-brand text-tremor-brand-inverted'
                  : 'text-tremor-content hover:text-tremor-content-strong')
              }
            >
              {t.pacesetter.tab.label} {id}
            </button>
            {tabs.length > 1 && (
              <button
                type="button"
                onClick={() => closeTab(id)}
                aria-label={t.pacesetter.tab.close}
                className="px-1.5 text-tremor-content hover:text-tremor-content-strong"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={addTab}
          disabled={tabs.length >= MAX_TABS}
          aria-label={t.pacesetter.tab.new}
          className="text-sm px-2 py-1 rounded text-tremor-content hover:text-tremor-content-strong disabled:opacity-40 disabled:cursor-not-allowed"
        >
          +
        </button>
      </div>
      {tabs.map((id) => (
        <div key={id} className={id === active ? 'flex-1 flex flex-col min-h-0' : 'hidden'}>
          <PacesetterTerminal
            active={id === active}
            onStatus={(s) => setStatuses((prev) => ({ ...prev, [id]: s }))}
          />
        </div>
      ))}
    </div>
  );
}

// The on-screen key bar: PF1-PF4, F5-F14, Esc, arrows, Enter. Each button sends
// the exact vt220 byte sequence and refocuses the terminal. F5/F11/F12 matter
// here because the browser reserves those physical keys (refresh/fullscreen/
// devtools) — the button is the reliable way to send them; F13/F14 have no
// physical key on most keyboards, so the button is the only way.
const KEY_BAR: (keyof typeof VT220_KEYS)[] = [
  'PF1', 'PF2', 'PF3', 'PF4', 'F5',
  'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12', 'F13', 'F14',
  'Esc', 'Up', 'Down', 'Left', 'Right', 'Enter',
];

function keyLabel(k: keyof typeof VT220_KEYS): string {
  const labels: Record<keyof typeof VT220_KEYS, string> = {
    PF1: t.pacesetter.keys.pf1, PF2: t.pacesetter.keys.pf2, PF3: t.pacesetter.keys.pf3, PF4: t.pacesetter.keys.pf4,
    F5: t.pacesetter.keys.f5,
    F6: t.pacesetter.keys.f6, F7: t.pacesetter.keys.f7, F8: t.pacesetter.keys.f8, F9: t.pacesetter.keys.f9,
    F10: t.pacesetter.keys.f10, F11: t.pacesetter.keys.f11, F12: t.pacesetter.keys.f12,
    F13: t.pacesetter.keys.f13, F14: t.pacesetter.keys.f14,
    Esc: t.pacesetter.keys.esc, Up: t.pacesetter.keys.up, Down: t.pacesetter.keys.down,
    Left: t.pacesetter.keys.left, Right: t.pacesetter.keys.right, Enter: t.pacesetter.keys.enter,
  };
  return labels[k];
}

interface PacesetterTerminalProps {
  /** Reports this terminal's connection status up to PacesetterTabs, which
   *  renders it as a dot in the tab strip. Optional so PacesetterTerminal
   *  stays usable standalone (e.g. in isolation) without a parent. */
  onStatus?: (status: ConnStatus) => void;
  /** True when this terminal's tab is the visible/active one. Drives
   *  auto-focus (see the focus effect below). Defaults to true so a
   *  standalone PacesetterTerminal (no tab parent) still auto-focuses. */
  active?: boolean;
}

function PacesetterTerminal({ onStatus, active = true }: PacesetterTerminalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<ConnStatus>('connecting');
  const [theme, setTheme] = useState<PacesetterTheme | null>(null);
  // Bumped every time the theme resolves (mount + each refetch) — used to
  // cache-bust the background-image URL below so a customer-wide appearance
  // edit (uploaded/changed from another tab) is actually visible here
  // instead of the browser quietly reusing its cached image bytes.
  const [themeVersion, setThemeVersion] = useState(0);
  // Bumped by the reconnect button to re-run the connection effect below —
  // a fresh terminal + socket per attempt, matching the backend's session
  // model (paceterm.Manager hands out a fresh child PTY per socket; there's
  // no session to resume).
  const [attempt, setAttempt] = useState(0);

  // Always call the LATEST onStatus without making it an effect dependency:
  // PacesetterTabs passes a fresh inline closure on every render (it's
  // defined inside a .map()), so depending on `onStatus` directly would
  // re-fire the status effect on every unrelated re-render of the parent —
  // and since that effect's job IS to trigger a parent re-render (it calls
  // setStatuses), that would loop forever. The ref sidesteps this: reading
  // the current callback is deferred to when `status` itself actually
  // changes, never when just the closure identity changes.
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;
  useEffect(() => {
    onStatusRef.current?.(status);
  }, [status]);

  // Fetch the customer-wide appearance on mount, AND re-fetch whenever the
  // window regains focus — this is a shared kiosk terminal, so an owner
  // editing appearance from another tab (Config.tsx) should show up here
  // without requiring a manual reload. A separate effect below applies
  // `theme` to the live terminal whenever it — or the terminal instance
  // itself, e.g. after a reconnect — changes. Best-effort: a failed fetch
  // just leaves the terminal on xterm's own defaults.
  useEffect(() => {
    let alive = true;
    function load() {
      getPacesetterTheme()
        .then((th) => { if (alive) { setTheme(th); setThemeVersion((v) => v + 1); } })
        .catch(() => { /* xterm defaults */ });
    }
    load();
    window.addEventListener('focus', load);
    return () => { alive = false; window.removeEventListener('focus', load); };
  }, []);

  // Terminal + WebSocket lifecycle. The terminal is a FIXED 80x24 grid — the
  // char-mode ERP (TERM=vt220) only ever draws a fixed 80x24 menu, so a
  // FitAddon that expands the terminal to fill the container just leaves a
  // large empty black area around it. No FitAddon at all: the mount div
  // below centers this fixed-size element via flexbox instead of resizing
  // the grid to match the container.
  useEffect(() => {
    if (!containerRef.current) return;
    setStatus('connecting');

    // scrollback: 0 — the char-mode ERP owns the whole fixed 80x24 screen and
    // never uses a scrollback buffer, so with no scroll region xterm 6.x's
    // ScrollableElement has nothing to scroll and keeps its overlay scrollbar
    // hidden (index.css hard-hides it too, as a backstop).
    const term = new Terminal({ allowTransparency: true, fontSize: 14, cursorBlink: true, cols: 80, rows: 24, scrollback: 0 });
    term.open(containerRef.current);
    termRef.current = term;

    const ws = new WebSocket(pacesetterWsUrl());
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    function wsSend(data: Uint8Array | string) {
      if (ws.readyState === WebSocket.OPEN) ws.send(data);
    }
    function sendResize() {
      wsSend(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
    }

    // The vt220 key-map shim: only the keys in KEYDOWN_MAP are taken over
    // (preventDefault + send the exact bytes ourselves + return false so
    // xterm doesn't also process it); everything else falls through to
    // xterm's own (already-correct-for-most-keys) handling.
    term.attachCustomKeyEventHandler((e) => {
      if (e.type !== 'keydown') return true;
      const bytes = vt220KeyForEvent(e.key);
      if (!bytes) return true;
      e.preventDefault();
      wsSend(toBytes(bytes));
      return false;
    });

    ws.onopen = () => { setStatus('connected'); sendResize(); };
    ws.onmessage = (ev) => term.write(new Uint8Array(ev.data as ArrayBuffer));
    ws.onclose = () => setStatus('closed');
    ws.onerror = () => setStatus('closed');

    const dataSub = term.onData((d) => wsSend(toBytes(d)));
    const resizeSub = term.onResize(() => sendResize());

    return () => {
      dataSub.dispose();
      resizeSub.dispose();
      ws.close();
      term.dispose();
      termRef.current = null;
      wsRef.current = null;
    };
  }, [attempt]);

  // Auto-focus the terminal's input when this tab is the active/visible one —
  // on first mount, after a reconnect (a brand-new Terminal via `attempt`), and
  // when the operator switches back to this tab (`active` flips true). Without
  // this the user has to click the black screen before the ERP receives any
  // keystrokes. Gated on `active` so a hidden (display:none) background tab
  // never steals focus from the visible one. requestAnimationFrame defers the
  // focus() to after the browser has laid out a just-unhidden element (calling
  // focus() while it's still display:none is a no-op).
  useEffect(() => {
    if (!active) return;
    const raf = requestAnimationFrame(() => termRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [active, attempt]);

  // Apply the fetched customer-wide appearance to the live terminal. Runs
  // again on `attempt` since reconnecting builds a brand-new Terminal.
  useEffect(() => {
    const term = termRef.current;
    if (!term || !theme) return;
    const { theme: colors, fontSize } = themeToXterm(theme);
    term.options.fontSize = fontSize;
    // A transparent xterm background lets the container's background image
    // + dim overlay (rendered below) show through underneath the text
    // (allowTransparency was set at construction) — see the design spec's
    // "Background image mechanics". The xterm-viewport/xterm-screen DOM
    // elements themselves are ALSO opaque by default (bundled xterm.css) —
    // that's handled separately by the .pacesetter-bg-image CSS override
    // (index.css) applied to the mount div below when has_image is true.
    // With a background image we want the xterm cells themselves transparent so
    // the container image shows through behind the text. xterm's colour parser
    // (css.toColor) only matches /#[\da-f]{3,8}/ (hex) and rgb(a) — the string
    // 'transparent' does NOT parse and silently falls back to OPAQUE black, so
    // use an 8-digit hex with zero alpha (#RRGGBBAA, AA=00). allowTransparency
    // (set at construction) makes the alpha honoured. The opaque .xterm-viewport
    // /.xterm-screen backgrounds from xterm.css are handled separately by the
    // .pacesetter-bg-image CSS override in index.css — both layers are needed.
    term.options.theme = { ...colors, background: theme.has_image ? '#00000000' : colors.background };
  }, [theme, attempt]);

  function onKeyBarClick(key: keyof typeof VT220_KEYS) {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(toBytes(VT220_KEYS[key]));
    termRef.current?.focus();
  }

  return (
    <main className="flex-1 flex flex-col min-h-0" data-testid="pacesetter-terminal">
      <div
        className="relative flex-1 min-h-0 bg-black"
        data-testid="pacesetter-bg"
        style={theme?.has_image ? {
          backgroundImage: `url(${pacesetterImageUrl()}?v=${themeVersion})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        } : undefined}
      >
        {/* Connection status moved to a dot in the tab strip (PacesetterTabs)
            — the old always-visible status-bar row was removed to reclaim
            vertical space. Reconnect now floats over the terminal itself
            instead of living in that removed row. */}
        {status === 'closed' && (
          <Button
            size="xs"
            variant="secondary"
            className="absolute left-2 top-2 z-10"
            onClick={() => setAttempt((n) => n + 1)}
          >
            {t.pacesetter.reconnect}
          </Button>
        )}
        {theme?.has_image && (
          <div className="absolute inset-0 pointer-events-none bg-black" style={{ opacity: theme.dim }} />
        )}
        {/* Fixed 80x24 grid (set on the Terminal above), centered in the
            available area — the bg image + dim overlay fill the space
            around it. pacesetter-xterm (index.css) is always present —
            it hides the always-on xterm-viewport scrollbar chrome (the
            fixed grid never actually scrolls). pacesetter-bg-image
            (index.css) is conditional — it only applies when there's
            actually a background image to show through the otherwise-
            opaque xterm viewport/screen elements. */}
        <div
          ref={containerRef}
          data-testid="pacesetter-xterm-container"
          className={
            'absolute inset-0 p-1 flex items-center justify-center pacesetter-xterm' +
            (theme?.has_image ? ' pacesetter-bg-image' : '')
          }
        />
      </div>
      <div className="px-4 py-2 flex flex-wrap gap-1.5 border-t border-tremor-border">
        {KEY_BAR.map((k) => (
          <Button key={k} size="xs" variant="secondary" onClick={() => onKeyBarClick(k)}>
            {keyLabel(k)}
          </Button>
        ))}
      </div>
    </main>
  );
}
